import React from 'react';
import {
  Banner,
  CondensedMedium,
  Flashy,
  Gutter,
  Icon,
  Layout,
  ParagraphHeader,
  Well,
} from '@axiom/ui';
import type { DsMatchSummary } from '@axiom/validation';

const getFitBannerType = (fitDecision: string) => {
  const normalized = fitDecision.toLowerCase();
  if (normalized.includes('excellent') || normalized.includes('good')) {
    return 'success' as const;
  }
  return normalized.includes('possible')
    ? ('warning' as const)
    : ('error' as const);
};

export const OpportunityTalentMatchSummaryReceived = ({
  data,
}: {
  data: NonNullable<DsMatchSummary['data']>;
}) => {
  const bannerType = getFitBannerType(data.fitDecision);

  const bannerIconColorMap = {
    success: 'dataVizForestGreen',
    warning: 'dataVizYellow',
    error: 'dataVizRed',
  } as const;

  return (
    <Layout direction="vertical" verticalGutter="16px">
      <Banner name="fit-decision-banner" type={bannerType}>
        <Layout position="left middle" horizontalGutter="8px">
          <Flashy color={bannerIconColorMap[bannerType]}>
            <Icon name="intelligence-filled" />
          </Flashy>
          <Flashy bold>
            <span>{data.fitDecision}</span>
          </Flashy>
        </Layout>
      </Banner>
      <Well name="top-match-points-well">
        <ParagraphHeader name="top-match-points-header">
          <Layout position="left middle" horizontalGutter="12px">
            <Flashy color="dataVizForestGreen">
              <Icon name="intelligence-filled" />
            </Flashy>
            <span>Top Match Points</span>
          </Layout>
        </ParagraphHeader>
        <Gutter top="8px">
          {data.topMatchPoints.length > 0 ? (
            <Layout direction="vertical" verticalGutter="4px">
              <ul>
                {data.topMatchPoints.map(item => (
                  <li key={item}>
                    <CondensedMedium name={item}>{item}</CondensedMedium>
                  </li>
                ))}
              </ul>
            </Layout>
          ) : (
            <Flashy italic name="top-match-points-no-data">
              No available data
            </Flashy>
          )}
        </Gutter>
      </Well>
      <Well name="concerns-well">
        <ParagraphHeader name="concerns-header">
          <Layout position="left middle" horizontalGutter="12px">
            <Flashy color="dataVizYellow">
              <Icon name="intelligence-filled" />
            </Flashy>
            <span>Concerns / Gaps</span>
          </Layout>
        </ParagraphHeader>
        <Gutter top="8px">
          {data.concerns.length > 0 ? (
            <Layout direction="vertical" verticalGutter="4px">
              <ul>
                {data.concerns.map(item => (
                  <li key={item}>
                    <CondensedMedium name={item}>{item}</CondensedMedium>
                  </li>
                ))}
              </ul>
            </Layout>
          ) : (
            <Flashy italic name="concerns-no-data">
              No available data
            </Flashy>
          )}
        </Gutter>
      </Well>
      <Well name="evidence-well">
        <ParagraphHeader name="evidence-header">
          <Layout position="left middle" horizontalGutter="12px">
            <Icon name="intelligence-filled" />
            <span>Evidence</span>
          </Layout>
        </ParagraphHeader>
        <Gutter top="8px">
          {data.evidence.length > 0 ? (
            <Layout direction="vertical" verticalGutter="4px">
              <ul>
                {data.evidence.map(item => (
                  <li key={item}>
                    <CondensedMedium name={item}>{item}</CondensedMedium>
                  </li>
                ))}
              </ul>
            </Layout>
          ) : (
            <Flashy italic name="evidence-no-data">
              No available data
            </Flashy>
          )}
        </Gutter>
      </Well>
      <Well name="draft-submission-notes-well">
        <ParagraphHeader name="draft-submission-notes-header">
          <Layout position="left middle" horizontalGutter="12px">
            <Icon name="intelligence-filled" />
            <span>Draft Submission Note</span>
          </Layout>
        </ParagraphHeader>
        <Gutter top="8px">
          {data.clientSubmissionDraft ? (
            <CondensedMedium name="draft-submission-notes">
              {data.clientSubmissionDraft}
            </CondensedMedium>
          ) : (
            <Flashy italic name="draft-submission-notes-no-data">
              No available data
            </Flashy>
          )}
        </Gutter>
      </Well>
    </Layout>
  );
};
